import React from 'react';
import AnimatedSection from '../components/AnimatedSection';

const PrivacyPage = () => {
  const lastUpdated = 'December 2025';

  return (
    <div data-testid="privacy-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <AnimatedSection>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-darkgreen mb-4">
              Privacy Policy
            </h1>
            <p className="font-subheading text-gray-500">
              Last updated: {lastUpdated}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="prose prose-lg max-w-none">
              <div className="space-y-12">
                <div>
                  <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                    Introduction
                  </h2>
                  <p className="font-body text-gray-600 leading-relaxed">
                    Green Palm Consulting ("we," "our," or "us") is committed to protecting your privacy. 
                    This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                    information when you visit our website or use our services. Please read this 
                    privacy policy carefully.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                    Information We Collect
                  </h2>
                  <p className="font-body text-gray-600 leading-relaxed mb-4">
                    We may collect information about you in a variety of ways, including:
                  </p>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-emerald rounded-full mt-2 flex-shrink-0"></span>
                      <span><strong>Personal Data:</strong> Name, email address, phone number, and property address that you voluntarily provide when filling out our contact forms.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-emerald rounded-full mt-2 flex-shrink-0"></span>
                      <span><strong>Property Information:</strong> Details about your property that you provide to receive an offer.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-emerald rounded-full mt-2 flex-shrink-0"></span>
                      <span><strong>Usage Data:</strong> Information our servers automatically collect when you access our website, such as IP address, browser type, and pages visited.</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                    How We Use Your Information
                  </h2>
                  <p className="font-body text-gray-600 leading-relaxed mb-4">
                    We use the information we collect to:
                  </p>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-emerald rounded-full mt-2 flex-shrink-0"></span>
                      <span>Evaluate your property and prepare cash offers</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-emerald rounded-full mt-2 flex-shrink-0"></span>
                      <span>Communicate with you regarding your inquiry or transaction</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-emerald rounded-full mt-2 flex-shrink-0"></span>
                      <span>Improve our website and services</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-emerald rounded-full mt-2 flex-shrink-0"></span>
                      <span>Comply with legal obligations</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                    Information Sharing
                  </h2>
                  <p className="font-body text-gray-600 leading-relaxed">
                    We do not sell, trade, or rent your personal information to third parties. 
                    We may share information with trusted service providers who assist us in 
                    operating our website and conducting our business, provided they agree to 
                    keep this information confidential. We may also disclose information when 
                    required by law or to protect our rights.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                    Data Security
                  </h2>
                  <p className="font-body text-gray-600 leading-relaxed">
                    We implement appropriate security measures to protect your personal information 
                    from unauthorized access, alteration, disclosure, or destruction. However, 
                    no method of transmission over the Internet is 100% secure, and we cannot 
                    guarantee absolute security.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                    Your Rights
                  </h2>
                  <p className="font-body text-gray-600 leading-relaxed mb-4">
                    Depending on your location, you may have certain rights regarding your personal data, including:
                  </p>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-emerald rounded-full mt-2 flex-shrink-0"></span>
                      <span>The right to access your personal data</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-emerald rounded-full mt-2 flex-shrink-0"></span>
                      <span>The right to correct inaccurate data</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-emerald rounded-full mt-2 flex-shrink-0"></span>
                      <span>The right to request deletion of your data</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-emerald rounded-full mt-2 flex-shrink-0"></span>
                      <span>The right to opt out of marketing communications</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                    Contact Us
                  </h2>
                  <p className="font-body text-gray-600 leading-relaxed">
                    If you have questions about this Privacy Policy or wish to exercise your rights, 
                    please contact us at:
                  </p>
                  <div className="mt-4 p-6 bg-gray-50 rounded-xl">
                    <p className="font-body text-gray-700">
                      <strong>Green Palm Consulting</strong><br />
                      30 N Gould St Ste R<br />
                      Sheridan, WY 82801<br />
                      Email: privacy@greenpalmconsulting.com<br />
                      Phone: +1 307-298-2879
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
